import { Button, Form, Select, InputNumber, Input, message, notification } from "antd";
import { HomeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PageBreadcrumb from "../../components/breadcrumb";
import ImagePicker from "../../components/form/ImagePicker";
import { useAddBankDepositMutation } from "../../services/bankDeposit";
import { useGetOpenBanksQuery } from "../../services/banks";
import { Home, DepositHistoryRoute } from "../../routes/routepath";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface BankDepositFormValues {
  bankId:       string;
  amount:       number;
  receiptImage: string;
  // remark?:      string;
}

const breadcrumbItems = [
  { label: <HomeOutlined />, to: Home },
  { label: "Bank Deposit" },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const BankDeposit = () => {
  const [form]   = Form.useForm<BankDepositFormValues>();
  const navigate = useNavigate();

  // Branch comes from the header's universal branch filter — no separate
  // filter on this form.
  const rawBranch = useSelector((state: any) => state.branch.selectedBranch);
  const selectedBranchId = typeof rawBranch === 'object' ? (rawBranch?._id || rawBranch?.id) : rawBranch;

  const { data: banksResp, isFetching: banksLoading } = useGetOpenBanksQuery();
  const [addBankDeposit, { isLoading: submitting }] = useAddBankDepositMutation();

  const bankOptions = ((banksResp as any)?.data || []).map((b: any) => ({
    value: b._id,
    label: b.accountNumber ? `${b.bankName} - ${b.accountNumber}` : b.bankName,
  }));

  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[]; errors: string[] }[] }) => {
    if (!errorFields?.length) return;
    const labels = errorFields.map(f => {
      const name = Array.isArray(f.name) ? f.name[0] : f.name;
      return String(name).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    });
    notification.error({
      message:     'Required Fields Missing',
      description: [...new Set(labels)].join(', '),
      placement:   'topRight',
      duration:    4,
    });
  };

  const onFinish = async (values: BankDepositFormValues) => {
    if (!selectedBranchId) {
      message.error('Please select a branch from the header first');
      return;
    }
    try {
      await addBankDeposit({
        branchId:     selectedBranchId,
        bankId:       values.bankId,
        amount:       values.amount,
        receiptImage: values.receiptImage,
        // remark:       values.remark,
      }).unwrap();
      message.success('Bank deposit added successfully');
      form.resetFields();
      navigate(DepositHistoryRoute);
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  return (
    <div className="bank-deposit-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Bank Deposit</h2>
        <PageBreadcrumb items={breadcrumbItems} />
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        className="bank-deposit-form"
      >
        <Form.Item name="bankId" label="Bank" rules={[{ required: true, message: 'Please select a bank' }]}>
          <Select
            placeholder="Select bank"
            loading={banksLoading}
            showSearch
            optionFilterProp="label"
            options={bankOptions}
          />
        </Form.Item>

        <Form.Item name="amount" label="Amount" rules={[{ required: true, message: 'Please enter amount' }]}>
          <InputNumber style={{ width: '100%' }} min={0} placeholder="Enter amount" />
        </Form.Item>

        <Form.Item name="receiptImage" label="Receipt" rules={[{ required: true, message: 'Please upload the receipt' }]}>
          <ImagePicker form={form} name="receiptImage" />
        </Form.Item>

        {/* <Form.Item name="remark" label="Remark" className="field-span-2">
          <Input.TextArea rows={3} placeholder="e.g. Cash deposited at branch" />
        </Form.Item> */}

        <div className="footer-buttons">
          <Button type="default" onClick={() => navigate(DepositHistoryRoute)}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" className="save-btn" loading={submitting}>
            {submitting ? 'Adding...' : 'Add Bank Deposit'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default BankDeposit;
